/**
 * Wizard de importação de lista CSV
 * Layout premium com sidebar azul escuro igual aos modais de onboarding e campanha
 * 5 etapas: Upload → Validação → Importação → Preview → Salvar
 */

import { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  FileSpreadsheet, 
  Upload, 
  CheckCircle2, 
  Import, 
  Variable, 
  Save,
  Check,
  Users,
  CheckCheck,
  Hash
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Etapas do wizard
import CSVUploadStep from './steps/CSVUploadStep';
import ContactValidationStep from './steps/ContactValidationStep';
import ContactImportStep from './steps/ContactImportStep';
import VariablesPreviewStep from './steps/VariablesPreviewStep';
import SaveListStep from './steps/SaveListStep';

// Hooks e utilitários
import { useListImport, ImportResult } from '@/hooks/useListImport';
import { CSVParseResult } from '@/lib/csv-utils';

// =====================================================
// TIPOS
// =====================================================

interface ImportListWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

type WizardStep = 'upload' | 'validation' | 'import' | 'preview' | 'save';

// Definição dos steps com ícones e descrições
const STEPS: { key: WizardStep; label: string; icon: React.ElementType; description: string }[] = [
  { key: 'upload', label: 'Upload', icon: Upload, description: 'Selecione o arquivo CSV com seus contatos' },
  { key: 'validation', label: 'Validação', icon: CheckCircle2, description: 'Verifique e corrija erros nos dados' },
  { key: 'import', label: 'Importação', icon: Import, description: 'Configure o nome e inicie a importação' },
  { key: 'preview', label: 'Variáveis', icon: Variable, description: 'Visualize as variáveis detectadas' },
  { key: 'save', label: 'Salvar', icon: Save, description: 'Confirme e salve a lista' }
];

// =====================================================
// COMPONENTE
// =====================================================

export default function ImportListWizard({ 
  open, 
  onOpenChange, 
  onComplete 
}: ImportListWizardProps) {
  // Hook de importação
  const {
    validatedRows,
    validationStats,
    isValidating,
    isImporting,
    progress,
    validateRows,
    importList,
    updateValidatedRow,
    reset
  } = useListImport();
  
  // Estado do wizard
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [csvData, setCsvData] = useState<CSVParseResult | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  // Permite avançar mesmo com linhas com erro: apenas os registros válidos serão importados
  const [importOnlyValid, setImportOnlyValid] = useState(false);
  const [listName, setListName] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  // Estado para o DDI padrão selecionado no upload
  const [defaultCountryCode, setDefaultCountryCode] = useState('55');
  
  // Índice da etapa atual
  const currentStepIndex = STEPS.findIndex(s => s.key === currentStep);
  const currentStepData = STEPS[currentStepIndex];
  
  // Calcular colunas de variáveis (excluindo telefone, nome, email)
  const variableColumns = useMemo(() => {
    if (!csvData?.headers) return [];
    const reservedColumns = ['telefone', 'phone', 'celular', 'nome', 'name', 'email', 'e-mail'];
    return csvData.headers.filter(h => !reservedColumns.includes(h.toLowerCase()));
  }, [csvData?.headers]);
  
  // Reset ao fechar
  const handleClose = useCallback(() => {
    reset();
    setCsvData(null);
    setCurrentStep('upload');
    setUpdateExisting(false);
    setAllowDuplicates(false);
    setImportOnlyValid(false);
    setListName('');
    setImportResult(null);
    setDefaultCountryCode('55');
    onOpenChange(false);
  }, [reset, onOpenChange]);
  
  // =====================================================
  // HANDLERS DE NAVEGAÇÃO
  // =====================================================
  
  // Etapa 1: Upload → Validação
  const handleUploadComplete = async (data: CSVParseResult, countryCode: string) => {
    setCsvData(data);
    setDefaultCountryCode(countryCode);
    setCurrentStep('validation');
    
    // Iniciar validação automaticamente com o código de país
    await validateRows(data.rows, updateExisting, allowDuplicates, countryCode);
  };
  
  // Etapa 2: Validação → Importação
  const handleValidationComplete = () => {
    // Quando "importar somente válidos" está ativo, linhas com erro são simplesmente
    // ignoradas pela rotina de importação (importList já filtra isValid/blacklist/duplicadas)
    if (importOnlyValid) {
      const importableCount = validatedRows.filter(v =>
        v.isValid && !v.isBlacklisted && (allowDuplicates || !v.isDuplicate)
      ).length;
      if (importableCount === 0) return;
      setCurrentStep('import');
      return;
    }

    // Verificar se há erros (duplicatas não contam se allowDuplicates)
    const hasErrors = validatedRows.some(v => 
      !v.isValid || (v.isDuplicate && !allowDuplicates)
    );
    if (hasErrors) return;
    
    setCurrentStep('import');
  };
  
  // Etapa 3: Importação → Preview
  const handleImportComplete = async () => {
    if (!csvData || !listName) return;
    
    const result = await importList(
      listName,
      csvData.rows,
      csvData.headers,
      validatedRows,
      updateExisting,
      allowDuplicates
    );
    
    setImportResult(result);
    
    if (result.success) {
      setCurrentStep('preview');
    }
  };
  
  // Etapa 4: Preview → Salvar
  const handlePreviewComplete = () => {
    setCurrentStep('save');
  };
  
  // Etapa 5: Finalizar
  const handleSaveComplete = () => {
    onComplete();
    handleClose();
  };
  
  // Voltar uma etapa
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].key);
    }
  };

  // Navegar para step específico (apenas se já completado)
  const handleStepClick = (stepKey: WizardStep) => {
    const targetIndex = STEPS.findIndex(s => s.key === stepKey);
    if (targetIndex < currentStepIndex) {
      setCurrentStep(stepKey);
    }
  };
  
  // =====================================================
  // RENDER
  // =====================================================
  
  const renderStep = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <CSVUploadStep
            onComplete={handleUploadComplete}
          />
        );
        
      case 'validation':
        return (
          <ContactValidationStep
            csvData={csvData}
            validatedRows={validatedRows}
            validationStats={validationStats}
            isValidating={isValidating}
            progress={progress}
            updateExisting={updateExisting}
            onUpdateExistingChange={setUpdateExisting}
            allowDuplicates={allowDuplicates}
            onAllowDuplicatesChange={setAllowDuplicates}
            importOnlyValid={importOnlyValid}
            onImportOnlyValidChange={setImportOnlyValid}
            onUpdateRow={updateValidatedRow}
            onRevalidate={() => csvData && validateRows(csvData.rows, updateExisting, allowDuplicates, defaultCountryCode)}
            onComplete={handleValidationComplete}
          />
        );
        
      case 'import':
        return (
          <ContactImportStep
            csvData={csvData}
            validatedRows={validatedRows}
            validationStats={validationStats}
            listName={listName}
            onListNameChange={setListName}
            isImporting={isImporting}
            progress={progress}
            onStartImport={handleImportComplete}
          />
        );
        
      case 'preview':
        return (
          <VariablesPreviewStep
            csvData={csvData}
            validatedRows={validatedRows}
            importResult={importResult}
            onComplete={handlePreviewComplete}
          />
        );
        
      case 'save':
        return (
          <SaveListStep
            importResult={importResult}
            listName={listName}
            csvData={csvData}
            onComplete={handleSaveComplete}
          />
        );
        
      default:
        return null;
    }
  };
  
  const StepIcon = currentStepData.icon;
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          
          {/* ===== SIDEBAR PREMIUM ===== */}
          <aside 
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>
            
            {/* Conteúdo da sidebar */}
            <div className="relative z-10 flex flex-col h-full p-5">
              
              {/* Header da sidebar */}
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Importar Lista</h2>
                <p className="text-sm text-white/60 mt-1">
                  Etapa {currentStepIndex + 1} de {STEPS.length}
                </p>
              </div>
              
              {/* Lista de Steps */}
              <nav className="flex-1 space-y-1">
                {STEPS.map((step, index) => {
                  const isCompleted = index < currentStepIndex;
                  const isActive = index === currentStepIndex;
                  const isPending = index > currentStepIndex;
                  const Icon = step.icon;
                  
                  return (
                    <button
                      key={step.key}
                      type="button"
                      onClick={() => handleStepClick(step.key)}
                      disabled={isPending || isValidating || isImporting}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200",
                        isActive && "bg-white/10 border border-white/20",
                        isCompleted && "hover:bg-white/5 cursor-pointer",
                        isPending && "opacity-50 cursor-not-allowed",
                        !isActive && !isPending && "hover:bg-white/5"
                      )}
                    >
                      {/* Círculo indicador */}
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 border-2",
                        isCompleted && "bg-green-500 border-green-500",
                        isActive && "bg-white border-white",
                        isPending && "bg-transparent border-white/30"
                      )}>
                        {isCompleted ? (
                          <Check className="h-4 w-4 text-white" />
                        ) : (
                          <Icon className={cn(
                            "h-4 w-4",
                            isActive ? "text-primary" : "text-white/60"
                          )} />
                        )}
                      </div>
                      
                      {/* Texto do step */}
                      <div className="flex-1 min-w-0">
                        <span className={cn(
                          "block text-sm font-medium truncate",
                          isActive ? "text-white" : "text-white/80"
                        )}>
                          {step.label}
                        </span>
                        <span className="block text-xs text-white/40 truncate">
                          {step.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </nav>
              
              {/* Resumo no rodapé */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">
                  Resumo
                </h3>
                <div className="space-y-2">
                  {/* Total de contatos */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Contatos</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {csvData?.totalRows || 0}
                    </span>
                  </div>
                  
                  {/* Contatos válidos */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCheck className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Válidos</span>
                    </div>
                    <span className={cn(
                      "text-sm font-medium",
                      validationStats?.valid > 0 ? "text-green-400" : "text-white"
                    )}>
                      {validationStats?.valid || 0}
                    </span>
                  </div>
                  
                  {/* Variáveis detectadas */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Variáveis</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {variableColumns.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
          
          {/* ===== CONTEÚDO PRINCIPAL ===== */}
          <main className="flex flex-col h-full min-h-0">
            
            {/* Header do step atual */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStepData.label}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStepData.description}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            {/* Área de conteúdo scrollável - com containment de largura agressivo */}
            <div 
              className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-6"
              style={{ maxWidth: '100%', width: '100%' }}
            >
              {renderStep()}
            </div>
            
            {/* Footer com navegação */}
            <div className="flex-shrink-0 bg-background px-6 py-4 border-t flex justify-between">
              <Button
                variant="outline"
                onClick={currentStepIndex === 0 ? handleClose : handleBack}
                disabled={isValidating || isImporting}
              >
                {currentStepIndex === 0 ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </>
                ) : (
                  <>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Voltar
                  </>
                )}
              </Button>
              
              {/* Indicador mobile do step atual */}
              <div className="flex md:hidden items-center gap-1">
                {STEPS.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all",
                      index < currentStepIndex && "bg-green-500",
                      index === currentStepIndex && "bg-primary w-4",
                      index > currentStepIndex && "bg-muted"
                    )}
                  />
                ))}
              </div>
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
